/**
 * The host the marketing site's scanner carries into signup (task 293).
 *
 * `/tools/site-scanner/` links to `/signup?site=<host>`. Descope's flow
 * redirects for social login and the query is gone by the time onboarding
 * renders, so the host is kept in `sessionStorage`: same origin, so it
 * survives those redirects, and gone when the tab closes. It is validated as
 * a hostname here and again by the site form, is never rendered unescaped,
 * and is never trusted beyond a pre-filled input.
 */
import { normalizeDomain, validateSiteForm } from "@/lib/validation/site";

const KEY = "onboarding:prefill-site";

function storage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    // Storage can throw in a private window or with site data blocked; the
    // pre-fill is a convenience and the form works without it.
    return undefined;
  }
}

/** A `?site=` value as a hostname the site form accepts, or undefined. */
export function hostnameFromQuery(search: string): string | undefined {
  const raw = new URLSearchParams(search).get("site");
  if (!raw || raw.length > 253) return undefined;
  const domain = normalizeDomain(raw);
  return validateSiteForm({ name: "site", domain }).domain ? undefined : domain;
}

/** Remember the host a signup link carried, if it carried a valid one. */
export function capturePrefilledSite(search: string): void {
  const host = hostnameFromQuery(search);
  if (host) storage()?.setItem(KEY, host);
}

/** The remembered host, if any. Left in place until the site is created. */
export function readPrefilledSite(): string | undefined {
  const value = storage()?.getItem(KEY) ?? undefined;
  // Re-validated on the way out: storage is writable by any script on the
  // origin, and the form is the only thing that should ever see it.
  return value && !validateSiteForm({ name: "site", domain: value }).domain
    ? value
    : undefined;
}

export function clearPrefilledSite(): void {
  storage()?.removeItem(KEY);
}
